import { motion } from 'framer-motion';
import { CheckCircle, Clock, Phone, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';
import ConsultationForm from '@/components/common/ConsultationForm';
import { usePageMeta } from '@/hooks/useSEO';

const benefits = [
  'Job search visa for 1 year',
  'Part-time work allowed (20 hrs/week)',
  'Path to EU Blue Card',
  'No job offer required initially',
  'Bring family after employment',
  'Gateway to EU job market',
];

const pointsSystem = [
  { criteria: 'Recognized Qualification', points: '4' },
  { criteria: '5+ Years Experience', points: '3' },
  { criteria: '2-5 Years Experience', points: '2' },
  { criteria: 'German B1 Level', points: '3' },
  { criteria: 'German A2 Level', points: '2' },
  { criteria: 'English C1 Level', points: '1' },
  { criteria: 'Age Under 35', points: '2' },
  { criteria: 'Previous Germany Stay', points: '1' },
];

const GermanyOpportunityPage = () => {
  usePageMeta(
    'Germany Opportunity Card from India 2026 | Chancenkarte Job Seeker Visa — Zafco HR',
    'Apply for Germany Opportunity Card (Chancenkarte) via Zafco HR — MEA-approved consultants. Points-based job seeker visa, 1-year stay, path to EU Blue Card. Free eligibility test.'
  );
  const openWhatsApp = () => {
    window.open('https://wa.me/919958848998?text=Hello%20Zafco,%20I%20am%20interested%20in%20Germany%20Opportunity%20Card', '_blank');
  };

  return (
    <div data-testid="germany-opportunity-page">
      <PageHeader 
        title="Germany Opportunity Card 2026"
        subtitle="Your gateway to European job opportunities through the Chancenkarte"
        breadcrumbs={[
          { label: 'Countries', path: '#' },
          { label: 'Germany', path: '#' },
          { label: 'Opportunity Card' }
        ]}
        backgroundImage="https://images.unsplash.com/photo-1549867060-246c2034d01b?w=1920"
      />

      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-zafco-teal font-semibold text-sm tracking-wider uppercase">
                Germany Job Seeker Visa
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-zafco-blue font-montserrat mt-2 mb-6">
                Germany Opportunity Card Job Placement
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                The Germany Opportunity Card (Chancenkarte) is a revolutionary points-based job seeker 
                visa allowing non-EU nationals to enter Germany for up to one year to search for employment. 
                You need minimum 6 points to qualify.
              </p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-gray-50 rounded-xl p-4">
                  <Clock className="w-6 h-6 text-zafco-teal mb-2" />
                  <p className="text-2xl font-bold text-zafco-blue">1 Year</p>
                  <p className="text-sm text-gray-600">Job Search Period</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <Briefcase className="w-6 h-6 text-zafco-teal mb-2" />
                  <p className="text-2xl font-bold text-zafco-blue">6 Points</p>
                  <p className="text-sm text-gray-600">Minimum Required</p>
                </div>
              </div>

              {/* Points Table */}
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-8">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-zafco-blue to-zafco-teal text-white">
                      <th className="px-4 py-3 text-left">Criteria</th>
                      <th className="px-4 py-3 text-right">Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pointsSystem.map((item) => (
                      <tr key={item.criteria} className="border-b">
                        <td className="px-4 py-3">{item.criteria}</td>
                        <td className="px-4 py-3 text-right font-bold text-zafco-blue">{item.points}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Button 
                onClick={openWhatsApp}
                className="bg-gradient-to-r from-zafco-gold to-zafco-orange text-white font-semibold px-8"
              >
                <Phone className="w-5 h-5 mr-2" />
                Calculate Your Points
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-xl"
            >
              <h3 className="text-xl font-bold text-zafco-blue font-montserrat mb-6">
                Free Germany Assessment
              </h3>
              <ConsultationForm />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit}
                className="bg-white rounded-xl p-6 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <CheckCircle className="w-8 h-8 text-green-500 mb-3" />
                <p className="text-gray-700 font-medium">{benefit}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-zafco-blue to-zafco-teal">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white font-montserrat mb-6">
            Start Your European Career Journey
          </h2>
          <Button 
            onClick={openWhatsApp}
            size="lg"
            className="bg-gradient-to-r from-zafco-gold to-zafco-orange text-white font-semibold px-10 py-7 text-lg"
          >
            <Phone className="w-6 h-6 mr-3" />
            Get Free Assessment
          </Button>
        </div>
      </section>
    </div>
  );
};

export default GermanyOpportunityPage;
